import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import FormRow from 'components/forms/FormRow';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput.tsx';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import InfoTooltip from 'components/InfoTooltip.tsx';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMattersWithFiltersQuery } from 'store/api/matters';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features.ts';
import { ProjectFormData } from 'types/projects';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { renderMatterIndicators, renderMatterOptionWithRestrictedSelection } from 'utils/matterOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';

/** What the project form's field groups read: the form, and the classifiers its selects list. */
export interface ProjectFormState {
    methods: UseFormReturn<ProjectFormData>;
    classifiers: Record<string, ClassifierData>;
}

export const ProjectOverviewFields = ({ form }: { form: ProjectFormState }): JSX.Element => {
    const { control } = form.methods;
    return (
        <>
            <TextInput control={control} name="projectName" label="projects.form.name" required />
            <TextAreaInput control={control} name="description" label="projects.form.description" required />
            <FormRow>
                <DateInput control={control} name="openedDate" label="projects.form.opened-date" required />
                <DateInput control={control} name="closedDate" label="projects.form.closed-date" />
            </FormRow>
        </>
    );
};

export const ProjectBillingFields = ({ form }: { form: ProjectFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const isNonBillable = useWatch({ control, name: 'isNonBillable' });
    const hasPermissionFieldAccessRemunerationTime = useHasPermissions(['FIELD_ACCESS.TIMECARD.REMUNERATION_TIME']);
    const hasPermissionUpdateMattersRemuneration = useHasPermissions(['UPDATE.MATTERS.REMUNERATION']);
    const hideDontNullifyOption = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);
    return (
        <>
            <CheckboxInput
                control={control}
                name="isNonBillable"
                label="projects.non-billable"
                onChange={(checked: boolean) => {
                    if (!checked) setValue('preserveNonBillableHours', false);
                }}
            />
            {isNonBillable && !hideDontNullifyOption ? (
                <CheckboxInput
                    control={control}
                    name="preserveNonBillableHours"
                    label="matters.preserve-non-billable-hours"
                    rightLabelComponent={<InfoTooltip text={'matters.tooltips.preserve-non-billable-hours'} />}
                />
            ) : null}
            {hasPermissionFieldAccessRemunerationTime && (
                <FeatureFlagged action="show" flag={FeatureFlag.REMUNERATION_CALC_TYPE_CONTROL}>
                    <CheckboxInput
                        control={control}
                        name="isUsedTimeUsedForRemuneration"
                        label="matters.use-used-time-for-remuneration"
                        disabled={!hasPermissionUpdateMattersRemuneration}
                    />
                </FeatureFlagged>
            )}
        </>
    );
};

export const ProjectTeamFields = ({ form }: { form: ProjectFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <>
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="projects.form.supervised-by"
                name="supervisedByEmployee"
                required
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="projects.form.responsible-lawyer"
                name="responsibleEmployee"
                required
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
            />
        </>
    );
};

export const ProjectCapFields = ({ form }: { form: ProjectFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <>
            <DecimalInput control={control} name="capLimit" required label="projects.form.cap-limit" />
            <SelectInput
                setValue={setValue}
                control={control}
                label="projects.form.cap-currency"
                name="capCurrencyType"
                required
                options={getValidOptionsFromClassifier<string, string | number>(form.classifiers['CURRENCY']) ?? []}
                getOptionLabel="label"
                getOptionIdentifier="value"
                sortDirection="asc"
            />
        </>
    );
};

export const ProjectCasesFields = ({ form }: { form: ProjectFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <DynamicMultiSelectInput
            setValue={setValue}
            control={control}
            label="projects.form.connected-cases"
            name="matters"
            paginated={{
                fetch: useLazyGetMattersWithFiltersQuery,
                searchField: ['fullName', 'clientParty.name'],
                sort: ['clientParty.name,asc', 'name,asc'],
                filters: 'projectMatters=isEmpty=true',
            }}
            groupByField="clientParty.name"
            getOptionLabel="fullName"
            getOptionIdentifier="id"
            getSelectedValueStartAdornment={renderMatterIndicators}
            customOptionRenderer={(option, props, getValue) =>
                renderMatterOptionWithRestrictedSelection(option, props, getValue, false)
            }
        />
    );
};
